// src/components/PrioritaOggi.js
// Sintesi, non un nuovo dato — prende quello che Nexiora già calcola in Centro Operativo,
// Cruscotto Scadenze e Sezione Commerciale, e sceglie da sola le 3 cose più urgenti da
// guardare oggi. Se non c'è nulla di davvero urgente, il widget semplicemente non compare.
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { FREQUENZE, prossimaScadenza, prossimaScadenzaVoce } from "./ManutenzioneTab";
import { calcolaSALComputo } from "../utils/calcoloSAL";
import { mappeEconomicheOrg, testoAggiornamentoCosti } from "../utils/economiaCommessa";
import { useOrg } from "../contexts/OrgContext";
import { fonteOreAzienda } from "../utils/oreCommessa";
import { vociChecklist, avvisoAttivo, testoAvviso } from "../utils/sicurezzaCantiere";

function dataItaliana(iso) { return iso ? new Date(iso+"T00:00:00").toLocaleDateString("it-IT") : "—"; }
function giorniDa(data) { return Math.round((data - new Date()) / 86400000); }

export default function PrioritaOggi({ orgId, onNavigate }) {
  const { canSeeEconomic, currentUser } = useAuth();
  const { orgVisitata } = useOrg();
  const fontOre = orgVisitata ? fonteOreAzienda(orgVisitata) : undefined;
  const [segnali, setSegnali] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const trovati = [];

      // Commesse a rischio margine — solo per chi vede dati economici, stesso dato di
      // Centro Operativo. Punteggio più alto quanto più il margine è vicino allo zero.
      if (canSeeEconomic) {
        const [projSnap, budgetSnap, movSnap, odaSnap] = await Promise.all([
          getDocs(query(collection(db,"projects"), where("organizationId","==",orgId))),
          getDocs(query(collection(db,"budgetCommessa"), where("organizationId","==",orgId))),
          getDocs(query(collection(db,"movimentiEconomici"), where("organizationId","==",orgId))),
          getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId))),
        ]);
        const progetti = projSnap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived && !p.isParent);
        // Stesse regole del Controllo Economico (utils/economiaCommessa.js)
        const { ricavi: budgetMap, costi: costiMap, aggiornati } = mappeEconomicheOrg({
          budget: budgetSnap.docs.map(d=>({id:d.id,...d.data()})), movimenti: movSnap.docs.map(d=>d.data()), ordini: odaSnap.docs.map(d=>d.data()), fontOre });
        progetti.forEach(p => {
          const budget = budgetMap[p.id]||0;
          if (budget<=0) return;
          const margine = (budget-(costiMap[p.id]||0))/budget*100;
          if (margine<15) {
            trovati.push({
              tipo:"margine", gravita: margine<5?"alta":"media",
              titolo:`${p.codiceCommessa?`[${p.codiceCommessa}] `:""}${p.name}`,
              dettaglio:`Margine sceso al ${(margine).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%${aggiornati[p.id] ? ` · costi al ${testoAggiornamentoCosti([aggiornati[p.id]])}` : ""}`, // W453
              punteggio: 100 - margine, // più basso il margine, più urgente
              onClick: ()=>onNavigate&&onNavigate({page:"project",projectId:p.id,project:p}),
            });
          }
        });
      }

      // Scadenze imminenti — Scadenzario + Manutenzione, prossimi 7 giorni o già scadute.
      const scadSnap = await getDocs(query(collection(db,"scadenzario"), where("organizationId","==",orgId)));
      scadSnap.docs.forEach(d => {
        const s = d.data();
        if (!s.dataScadenza || s.completata) return;
        const gg = giorniDa(new Date(s.dataScadenza+"T00:00:00"));
        if (gg<=7) trovati.push({
          tipo:"scadenza", gravita: gg<0?"alta":gg<=2?"alta":"media",
          titolo: s.nome||s.descrizione||"Scadenza",
          dettaglio: gg<0?`Scaduta da ${Math.abs(gg)}gg`:gg===0?"Scade oggi":`Scade tra ${gg}gg`,
          punteggio: 90 - gg,
          onClick: ()=>onNavigate&&onNavigate({page:"scadenzario"}),
        });
      });

      // SAL fermi e crediti in sospeso — stessa fonte, un solo giro sui Computi Metrici.
      const salSnap = await getDocs(query(collection(db,"computiSAL"), where("orgId","==",orgId)));
      salSnap.docs.forEach(d => {
        const s = d.data();
        const periodi = s.periodi||[];
        if (periodi.length===0) return;
        const ultimo = periodi[periodi.length-1];
        const ggFermo = giorniDa(new Date(ultimo.data+"T00:00:00"));
        if (ggFermo>30) trovati.push({
          tipo:"sal", gravita: ggFermo>60?"alta":"media",
          titolo:`${s.projectName||"Commessa"}`,
          dettaglio:`SAL fermo da ${Math.abs(ggFermo)}gg`,
          punteggio: 50 + Math.min(ggFermo,90),
          onClick: ()=>onNavigate&&onNavigate({page:"project",projectId:s.projectId,project:{id:s.projectId,name:s.projectName},initialTab:"sal"}),
        });
        if (canSeeEconomic) {
          // Solo il maturato che nessuno ha ancora chiesto di fatturare: le richieste già
          // inviate sono in mano all'amministrazione (utils/fatturazioneCommessa.js)
          const dati = calcolaSALComputo(s);
          if (dati.nonRichiesto>500 && ggFermo>15) trovati.push({
            tipo:"credito", gravita: dati.nonRichiesto>5000?"alta":"media",
            titolo:`${s.projectName||"Commessa"}`,
            dettaglio:`€${dati.nonRichiesto.toLocaleString("it-IT",{maximumFractionDigits:0})} maturati da chiedere in fattura`,
            punteggio: Math.min(dati.nonRichiesto/100, 80),
            onClick: ()=>onNavigate&&onNavigate({page:"registro-sal"}),
          });
        }
      });

      // Manutenzione a capitolato — ogni voce con la propria scadenza, non un'unica per
      // piano: è il dato che un PM navigato vuole vedere qui, in cima, non solo aprendo
      // Scadenzario apposta. Stessa soglia (14 giorni) già usata nella scheda dedicata.
      const [pianiSnap, interventiSnap, impiantiSnap, projSnap2] = await Promise.all([
        getDocs(query(collection(db,"manutenzionePiani"), where("organizationId","==",orgId), where("modalita","==","capitolato"))),
        getDocs(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",orgId))),
        getDocs(query(collection(db,"impianti"), where("organizationId","==",orgId))),
        getDocs(query(collection(db,"projects"), where("organizationId","==",orgId))),
      ]);
      const tuttiInterventi = interventiSnap.docs.map(d=>d.data());
      const impiantiMap = {}; impiantiSnap.docs.forEach(d=>impiantiMap[d.id]=d.data());
      const projMap = {}; projSnap2.docs.forEach(d=>projMap[d.id]={id:d.id,...d.data()});
      pianiSnap.docs.forEach(d => {
        const piano = d.data();
        const impianto = impiantiMap[piano.impiantoId];
        const project = projMap[impianto?.projectId || piano.projectId];
        if (!project) return;
        const interventiPiano = tuttiInterventi.filter(iv=>iv.pianoId===d.id);
        (piano.voci||[]).forEach(voce => {
          const scad = prossimaScadenzaVoce(voce, piano, interventiPiano);
          if (!scad) return; // voce N.P.: non programmata su questo impianto
          const gg = giorniDa(scad);
          if (gg>14) return;
          trovati.push({
            tipo:"manutenzione", gravita: gg<0?"alta":gg<=3?"alta":"media",
            titolo: `${project.codiceCommessa?`[${project.codiceCommessa}] `:""}${voce.testo}`,
            dettaglio: `${impianto?.nome||project.name} — ${gg<0?`scaduta da ${Math.abs(gg)}gg`:gg===0?"scade oggi":`tra ${gg}gg`}`,
            punteggio: 85 - gg,
            onClick: ()=>onNavigate&&onNavigate({page:"project",projectId:project.id,project,initialTab:"manutenzione"}),
          });
        });
      });

      // Sicurezza di cantiere: i cantieri di cui sono il responsabile, con adempimenti aperti.
      // Massima priorità: il responsabile deve vederli appena entra.
      try {
        const sicSnap = await getDocs(query(collection(db,"sicurezzaCantiere"), where("organizationId","==",orgId), where("responsabileUid","==",currentUser?.uid||"-")));
        if (!sicSnap.empty) {
          const orgSnap = await getDoc(doc(db,"organizations",orgId));
          const vociAz = orgSnap.exists() ? orgSnap.data().sicurezzaVociAzienda : [];
          sicSnap.docs.forEach(d => {
            const sc = d.data();
            const p = projMap[sc.projectId];
            if (!p || p.archived || p.statoCommessa==="chiusa") return;
            const a = avvisoAttivo({ ...sc, avviso: null }, vociChecklist(p, sc.config, vociAz)); // per il responsabile conta tutto ciò che è aperto
            if (!a.attivo) return;
            trovati.push({
              tipo:"sicurezza", gravita:"alta",
              titolo:`${p.codiceCommessa?`[${p.codiceCommessa}] `:""}${p.name}`,
              dettaglio: testoAvviso(a),
              punteggio: 200 + (a.daConfigurare ? 50 : a.aperte),
              onClick: ()=>onNavigate&&onNavigate({page:"project",projectId:p.id,project:p,initialTab:"sicurezza"}),
            });
          });
        }
      } catch (e) { console.warn("Sicurezza di cantiere non letta:", e); }

      trovati.sort((a,b)=>b.punteggio-a.punteggio);
      setSegnali(trovati.slice(0,3));
      setCaricato(true);
    })();
  }, [orgId, canSeeEconomic, currentUser, fontOre]);

  if (!caricato || segnali.length===0) return null;

  const ICONE = { sicurezza:"🦺", margine:"⚠️", scadenza:"📅", sal:"📐", credito:"💰", manutenzione:"🔧" };
  const COLORI = { alta:{bg:"#fef2f2",bd:"#fecaca",dot:"#dc2626"}, media:{bg:"#fffbeb",bd:"#fde68a",dot:"#d97706"} };

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:18,marginBottom:16}}>
      <h3 style={{fontSize:13,fontWeight:800,color:"#1e1b4b",marginBottom:2}}>🎯 Le 3 cose di oggi</h3>
      <p style={{fontSize:11,color:"#9ca3af",marginBottom:10}}>Scelte da sola tra tutto quello che Nexiora già tiene d'occhio.</p>
      {segnali.map((s,i) => {
        const c = COLORI[s.gravita];
        return (
          <div key={i} onClick={s.onClick} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:c.bg,border:`1px solid ${c.bd}`,borderRadius:8,marginBottom:i<segnali.length-1?6:0,cursor:s.onClick?"pointer":"default"}}>
            <span style={{fontSize:15,flexShrink:0}}>{ICONE[s.tipo]}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12.5,fontWeight:700,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.titolo}</div>
              <div style={{fontSize:11,color:"#6b7280"}}>{s.dettaglio}</div>
            </div>
            <div style={{width:8,height:8,borderRadius:"50%",background:c.dot,flexShrink:0}}/>
          </div>
        );
      })}
      {onNavigate && (
        <button onClick={()=>onNavigate({page:"control-center"})}
          style={{width:"100%",marginTop:10,padding:"6px",background:"none",border:"none",borderTop:"1px solid #f3f4f6",cursor:"pointer",fontSize:11,color:"#7c3aed",fontWeight:600}}>
          Vedi tutto in Centro Operativo →
        </button>
      )}
    </div>
  );
}
